import { Vector3 } from 'three';
import type { Float32BufferAttribute } from 'three';
import type { JsonValue } from 'type-fest';

import ViewPort from '../ViewPort';
import { ENTITY_TYPES, PART_TYPES, SPLIT_CHAR } from '../constants';
import type { FaceJson, VectorJson } from '../contracts/basic';
import type { AssemblyInstance } from '../loaders/assemblyInstance';
import type { AnyBody, Body } from '../loaders/body';
import { STEPBody } from '../loaders/step/STEPBody';
import { STLBody } from '../loaders/stl/STLBody';
import { maxSTLVertices } from '../loaders/stl/STLGeometry';
import type { AnyCaliperObject } from './caliper-utils';
import type {
  AnyIntersectionTest,
  AnyIntersectionObject,
  IntersectionObject,
} from './intersection-utils';
import type { RendererState } from './renderer';

export type BodyPose = Record<string, { visibility: boolean }>; //A record of body or assemblyInstance `id` to group's visibility

export const getBodyPose = (
  bodiesAndAssemblyInstances: Iterable<AnyBody | AssemblyInstance>,
): BodyPose => {
  const bodyPose: BodyPose = {};
  for (const { id, group, isMaterial } of bodiesAndAssemblyInstances) {
    if (!isMaterial) {
      bodyPose[id as string] = {
        visibility: group.visible,
      };
    }
  }
  return bodyPose;
};

export type EncodedVector = { x: string; y: string; z: string };

const encodeVector = (vector: VectorJson, decimalPlaces = 4): EncodedVector => {
  return {
    x: vector.x.toFixed(decimalPlaces),
    y: vector.y.toFixed(decimalPlaces),
    z: vector.z.toFixed(decimalPlaces),
  };
};

type EncodedBodyPose = Record<string, { v: boolean }>;

const encodeBodyPose = (bodyPoseInformation: BodyPose): EncodedBodyPose => {
  const encodedInfo: EncodedBodyPose = {};
  for (const bodyName in bodyPoseInformation) {
    encodedInfo[bodyName] = {
      v: bodyPoseInformation[bodyName].visibility,
    };
  }
  return encodedInfo;
};

export type LiteObject = {
  t: ENTITY_TYPES;
  obj: string;
  p?: EncodedVector | null;
  f?: number | null;
  face?: FaceJson | null;
};

//encodeObject() is used to encode information for storage in JSON
//* Note the `f` index is used in cadViewer/data/reducer.js, cadViewer/addons/SelectedObjectsDisplay.js, and pcad/cadViewer/data/reducer.js
export const encodeObject = (
  obj: Required<AnyIntersectionTest>,
): LiteObject => ({
  t: obj.type,
  obj: obj.obj?.name ?? '',
  p: obj.point && encodeVector(obj.point),
  //f is the faceIndex
  //* By convention, the index is the face's `a` value, which is the position of the first vertex in BufferGeometry
  //  position attribute
  //  !!! However before three.js release r125, f was the index of face in Object.Geometry, and it must be converted
  //  when parsing the encoded object below in parseSelectedObjects() !!!
  //* In the case of STL, the face's indices are already converted to be relative to the STL and not it's specific geometryBuffer
  f: obj.face ? obj.face.a : null,
  face: obj.face || null,
});

export const encodeViewerState = (state: RendererState): LitState => {
  const {
    bodyPoseInformation,
    cameraPosition,
    cameraUp,
    cameraZoom,
    controlTarget,
    maxBoundingBoxDiagonal,
    nodeHeight,
    nodeWidth,
    orthoView,
  } = state;
  const encodedState: LitState = {
    bpi: bodyPoseInformation && encodeBodyPose(bodyPoseInformation),
    cam: {
      ct: controlTarget && encodeVector(controlTarget),
      p: cameraPosition && encodeVector(cameraPosition),
      u: cameraUp && encodeVector(cameraUp),
      v: {
        l:
          typeof orthoView.left === 'number'
            ? orthoView.left.toFixed(4)
            : undefined,
        r:
          typeof orthoView.right === 'number'
            ? orthoView.right.toFixed(4)
            : undefined,
        b:
          typeof orthoView.bottom === 'number'
            ? orthoView.bottom.toFixed(4)
            : undefined,
        t:
          typeof orthoView.top === 'number'
            ? orthoView.top.toFixed(4)
            : undefined,
      },
      z: typeof cameraZoom === 'number' ? cameraZoom.toFixed(4) : undefined,
    },
    mbbd: maxBoundingBoxDiagonal.toFixed(4),
    n: {
      h: nodeHeight.toFixed(),
      w: nodeWidth.toFixed(),
    },
  };
  return encodedState;
};

//Note: encodeViewerPose is imported into src/modules/common/viewer/cadViewer/CADViewer.js
export const encodeViewerPose = (
  encodedViewerState: EncodedStateAndProps['encodedViewerState'],
  encodedViewerProps: EncodedStateAndProps['encodedViewerProps'],
): string => {
  return JSON.stringify({
    es: encodedViewerState,
    ep: encodedViewerProps,
  });
};

const parseVector = (obj: NonNullable<LiteObject['p']>): Vector3 =>
  new Vector3(parseFloat(obj.x), parseFloat(obj.y), parseFloat(obj.z));

export const parseSelectedObjects = (
  liteObjects: Iterable<LiteObject>,
  bodyOrAssemblyInstanceById: Map<string, AnyBody | AssemblyInstance>,
): AnyCaliperObject[] | undefined => {
  const out: AnyCaliperObject[] = [];
  for (const liteObject of liteObjects) {
    //Note: liteObject is created by encodeObject
    const { f: fIndex, obj: objectTag, p: litePoint, t: type } = liteObject;
    const face:
      | (FaceJson & {
          vertices?: [Vector3, Vector3, Vector3];
        })
      | null = liteObject.face
      ? liteObject.face
      : Number.isFinite(fIndex)
      ? //Then liteObject was created before upgrade to three.js release r125
        //* In which case fIndex is the face index in a three.js Geometry object and it
        //  needs to be transformed into a Face3-like object with properties a, b, and c; which are
        //  indices of the face in BufferGeometry position attribute
        //* Below face.normal will be added to this Face3-like object
        ({
          a: (fIndex as number) * 3,
          b: (fIndex as number) * 3 + 1,
          c: (fIndex as number) * 3 + 2,
        } as FaceJson)
      : null;

    const point = (litePoint && parseVector(litePoint)) as Vector3;
    const [bodyId, typeTag, geometriesIndexAsString] = objectTag.split(
      SPLIT_CHAR,
    ) as [string, ENTITY_TYPES, string?];

    const body = bodyOrAssemblyInstanceById.get(bodyId) as AnyBody | undefined;
    if (!body) {
      console.warn('Body Not Found');
      return;
    }
    if (!body.isLoaded) {
      console.warn('Body is not loaded');
      return;
    }
    let intersectObject: AnyIntersectionObject;
    if (typeTag === ENTITY_TYPES.SimpRep) {
      if (!body.obbSimplifiedRep) {
        continue;
      }
      intersectObject = body.obbSimplifiedRep;
    } else if (body.obj instanceof STEPBody) {
      intersectObject = body.obj
        .getObjsByType(typeTag)
        ?.get(objectTag) as IntersectionObject<PART_TYPES.STEP>;
    } else {
      if (!(body.obj instanceof STLBody)) {
        console.warn('Wrong Body Type');
        return;
      }
      const geometriesIndex = parseInt(geometriesIndexAsString ?? '0', 10);
      intersectObject = body.obj.meshes[geometriesIndex];
      if (face && !face.vertices) {
        const geometry = (body as Body<PART_TYPES.STL>).part.geometry
          .geometries[geometriesIndex];
        const position = geometry.attributes.position as Float32BufferAttribute;
        // In the case of STL, the face's indices are relative to the STL and not it's specific geometryBuffer
        // So the converted to be relative to the geometryBuffer.
        const a = new Vector3().fromBufferAttribute(
          position,
          face.a % maxSTLVertices,
        );
        const b = new Vector3().fromBufferAttribute(
          position,
          face.b % maxSTLVertices,
        );
        const c = new Vector3().fromBufferAttribute(
          position,
          face.c % maxSTLVertices,
        );
        //Notes:
        //* face.vertices is used in renderer-component.js. See `const { vertices } = face;`
        //  The vertices are used to calculate `cumulativeInfo`
        //* intersection-utils.js also sets face.vertices
        face.vertices = [a, b, c];
        if (!face.normal) {
          //Special case where face was created above because the liteObject
          //was created before upgrade to three.js release r125. (See note above.)
          const cb = new Vector3().subVectors(c, b);
          const ab = new Vector3().subVectors(a, b);
          face.normal = cb.cross(ab).normalize();
        }
      }
    }
    out.push({
      body: bodyId,
      face: face,
      obj: intersectObject,
      point,
      type,
    });
  }
  return out;
};

type BodyPoseInfo = Record<string, { visibility: boolean }>;

const parseBodyPoseInfo = (liteBodyPoseInfo: EncodedBodyPose): BodyPoseInfo => {
  const parsedInfo: BodyPoseInfo = {};
  for (const bodyName in liteBodyPoseInfo) {
    parsedInfo[bodyName] = {
      visibility: liteBodyPoseInfo[bodyName].v,
    };
  }
  return parsedInfo;
};

export type CameraLite = {
  ct?: EncodedVector; //controlTarget
  p?: EncodedVector; //cameraPosition
  u?: EncodedVector; //cameraUp
  v: {
    l?: string; //orthoView.left as string
    r?: string; //orthoView.right as string
    b?: string; //orthoView.bottom as string
    t?: string; //orthoView.top as string
  };
  z?: string; //cameraZoom as string
  additional?: JsonValue;
};

type NodeLite = {
  h: string; //node height as string
  w: string; //node width as string
};

export type LitState = {
  bpi?: EncodedBodyPose;
  cam: CameraLite;
  mbbd: string;
  n: NodeLite;
};

export type DecodedState = {
  isForFeature: boolean;
  cameraPosition?: Vector3;
  cameraUp?: Vector3;
  cameraZoom?: number;
  controlTarget?: Vector3;
  orthoView?: ViewPort;
  additionalCamInfo?: JsonValue;
  bodyPoseInformation?: BodyPoseInfo;
  maxBoundingBoxDiagonal?: number;
  nodeHeight?: number;
  nodeWidth?: number;
};

export const decodeViewerState = (liteState: LitState): DecodedState => {
  const {
    bpi: bodyPoseInformationLite,
    cam: cameraLite,
    mbbd: maxBoundingBoxDiagonalLite,
    n: nodeLite,
  } = liteState;

  // first decode information that does not require renderer context
  const cameraPosition =
    cameraLite && cameraLite.p && parseVector(cameraLite.p);
  const cameraUp = cameraLite && cameraLite.u && parseVector(cameraLite.u);
  const cameraZoom = cameraLite && cameraLite.z && parseFloat(cameraLite.z);
  const controlTarget =
    cameraLite && cameraLite.ct && parseVector(cameraLite.ct);
  const orthoView =
    cameraLite &&
    cameraLite.v &&
    ViewPort.fromObject({
      left:
        typeof cameraLite.v.l === 'string' ? parseFloat(cameraLite.v.l) : NaN,
      right:
        typeof cameraLite.v.r === 'string' ? parseFloat(cameraLite.v.r) : NaN,
      bottom:
        typeof cameraLite.v.b === 'string' ? parseFloat(cameraLite.v.b) : NaN,
      top:
        typeof cameraLite.v.t === 'string' ? parseFloat(cameraLite.v.t) : NaN,
    });
  // used for snapping to features and feedback
  const additionalCamInfo = cameraLite && cameraLite.additional;
  const isForFeature = additionalCamInfo ? true : false;
  const bodyPoseInformation =
    bodyPoseInformationLite && parseBodyPoseInfo(bodyPoseInformationLite);
  const maxBoundingBoxDiagonal =
    maxBoundingBoxDiagonalLite && parseFloat(maxBoundingBoxDiagonalLite);
  const nodeHeight = nodeLite && parseFloat(nodeLite.h);
  const nodeWidth = nodeLite && parseFloat(nodeLite.w);

  // now construct an object that ignores null or undefined objects so we can have
  // partial state updates without resetting non passed in variables
  const decodedState: DecodedState = { isForFeature };
  if (cameraPosition) decodedState.cameraPosition = cameraPosition;
  if (cameraUp) decodedState.cameraUp = cameraUp;
  if (cameraZoom) decodedState.cameraZoom = cameraZoom;
  if (controlTarget) decodedState.controlTarget = controlTarget;
  if (orthoView) decodedState.orthoView = orthoView;
  if (additionalCamInfo) decodedState.additionalCamInfo = additionalCamInfo;
  if (bodyPoseInformation)
    decodedState.bodyPoseInformation = bodyPoseInformation;
  if (maxBoundingBoxDiagonal)
    decodedState.maxBoundingBoxDiagonal = maxBoundingBoxDiagonal;
  if (nodeHeight) decodedState.nodeHeight = nodeHeight;
  if (nodeWidth) decodedState.nodeWidth = nodeWidth;

  return decodedState;
};

type EncodedStateAndProps = {
  encodedViewerState: LitState;
  encodedViewerProps: Record<string, unknown>; //it is always {} today
};

export const getEncodedViewerStateAndPropsFromString = (
  viewerPoseString: string,
): EncodedStateAndProps => {
  const encodedPose = JSON.parse(viewerPoseString);
  const { es: encodedViewerState, ep: encodedViewerProps } = encodedPose;
  return {
    encodedViewerState,
    encodedViewerProps,
  };
};
